import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useUI } from '@/hooks/ui/useUI';
import { useUnsavedChangesWarning } from '@/hooks/ui/navigation';
import { useTranslation } from '@/i18n';
import { attendanceConfigurationService, mapAttendanceConfigurationFormToUpdate } from '@/api/domains/attendance';
import {
  markingEventService,
  companyControlsService,
  incidentNotificationRecipientsService,
  roleCatalogService,
} from '@/api/domains/catalog';
import type { AttendanceConfiguration } from '@/api/domains/attendance';
import type { SelectorOption } from '@/components/ui/inputs/selector/Selector.types';
import {
  attendanceConfigurationSchema,
  type AttendanceConfigurationFormValues,
  type AttendanceConfigForm,
} from '@/schemas/attendance/attendanceConfiguration.schema';


const DEFAULT_VALUES: AttendanceConfigurationFormValues = {
  geoOfflineRequired: false,
  collationMarkings: false,
  geoWebMarking: false,
  disableGeoMarking: false,
  geoInconsistencyAlert: false,
  employeeMarkingApproval: false,
  approvalDeadlineHours: null,
  todayMarkingModification: false,
  eventDefinitionMethod: '',
  entryEvent: null,
  exitEvent: null,
  collationTolerance: null,
  collationEntryEvent: null,
  collationExitEvent: null,
  multiCutsSameMonth: false,
  sendByDateConcept: false,
  groupClosure: false,
  holidaysAsWorkdays: false,
  art38IgnoreHolidays: false,
  calendarScheduleManagement: false,
  halfDayAbsenteeism: '',
  calculationByContractEnd: false,
  entryAbsenceMinutes: null,
  exitAbsenceMinutes: null,
  repeatedMarkingWait: null,
  absenceValidationControls: [],
  notifyAbsenceEmployee: false,
  notifyAbsenceSupervisor: false,
  notifyNoTimeControl: false,
  notifyAbsenceAdmin: false,
  adminAbsenceEmail: '',
  notifyDisconnectedDevice: false,
  disconnectedDeviceEmail: '',
  markingIncidentRecipient: null,
  notifyEntryDelay: false,
  entryDelayToleranceMinutes: null,
  notifyExitDelay: false,
  exitDelayToleranceMinutes: null,
  contractCompanyAbsence: false,
  massAttendanceEmail: '',
  dtFiscalizationEmails: '',
  excessHoursEmail: '',
  autoFillHolidays: false,
  autoFillVacations: false,
  autoFillSickLeave: false,
  autoFillSpecialPermissions: false,
  autoFillAdminPermissions: false,
  autoFillFractionPermissions: false,
  approvalFlow: false,
  reviewerProfiles: [],
  approverProfiles: [],
  automaticMode: '',
  cutDaysSlack: null,
};

type TabId = 'general' | 'calculation' | 'notifications' | 'validations';

/** Maps each form field to its tab ID for error-based tab redirection */
const FIELD_TAB_MAP: Partial<Record<keyof AttendanceConfigurationFormValues, TabId>> = {
  // General
  geoOfflineRequired: 'general', collationMarkings: 'general', geoWebMarking: 'general',
  disableGeoMarking: 'general', geoInconsistencyAlert: 'general', employeeMarkingApproval: 'general',
  approvalDeadlineHours: 'general', todayMarkingModification: 'general', eventDefinitionMethod: 'general',
  entryEvent: 'general', exitEvent: 'general', collationTolerance: 'general',
  collationEntryEvent: 'general', collationExitEvent: 'general',
  // Calculation
  multiCutsSameMonth: 'calculation', sendByDateConcept: 'calculation', groupClosure: 'calculation',
  holidaysAsWorkdays: 'calculation', art38IgnoreHolidays: 'calculation', calendarScheduleManagement: 'calculation',
  halfDayAbsenteeism: 'calculation', calculationByContractEnd: 'calculation',
  // Notifications
  entryAbsenceMinutes: 'notifications', exitAbsenceMinutes: 'notifications', repeatedMarkingWait: 'notifications',
  absenceValidationControls: 'notifications', notifyAbsenceEmployee: 'notifications',
  notifyAbsenceSupervisor: 'notifications', notifyNoTimeControl: 'notifications',
  notifyAbsenceAdmin: 'notifications', adminAbsenceEmail: 'notifications',
  notifyDisconnectedDevice: 'notifications', disconnectedDeviceEmail: 'notifications',
  markingIncidentRecipient: 'notifications', notifyEntryDelay: 'notifications',
  entryDelayToleranceMinutes: 'notifications', notifyExitDelay: 'notifications',
  exitDelayToleranceMinutes: 'notifications', contractCompanyAbsence: 'notifications',
  massAttendanceEmail: 'notifications', dtFiscalizationEmails: 'notifications',
  excessHoursEmail: 'notifications',
  // Validations
  autoFillHolidays: 'validations', autoFillVacations: 'validations', autoFillSickLeave: 'validations',
  autoFillSpecialPermissions: 'validations', autoFillAdminPermissions: 'validations',
  autoFillFractionPermissions: 'validations', approvalFlow: 'validations',
  reviewerProfiles: 'validations', approverProfiles: 'validations',
  automaticMode: 'validations', cutDaysSlack: 'validations',
};

const TAB_ORDER: TabId[] = ['general', 'calculation', 'notifications', 'validations'];

export interface UseAttendanceConfigurationReturn {
  configuration: AttendanceConfiguration | null;
  isLoading: boolean;
  isSaving: boolean;
  markingEventOptions: SelectorOption[];
  companyControlOptions: SelectorOption[];
  recipientOptions: SelectorOption[];
  roleOptions: SelectorOption[];
  eventDefinitionMethodOptions: SelectorOption[];
  halfDayAbsenteeismOptions: SelectorOption[];
  automaticModeOptions: SelectorOption[];
  form: AttendanceConfigForm;
  isDirty: boolean;
  handleSave: () => void;
  handleCancel: () => void;
  confirmNavigation: () => void;
}

interface UseAttendanceConfigurationOptions {
  onValidationError?: (tabId: TabId) => void;
}

export function useAttendanceConfiguration(
  options: UseAttendanceConfigurationOptions = {},
): UseAttendanceConfigurationReturn {
  const { showError, showSuccess, showApiError } = useUI();
  const { t: translate } = useTranslation('attendance');
  const { onValidationError } = options;

  const [configuration, setConfiguration] = useState<AttendanceConfiguration | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [markingEventOptions, setMarkingEventOptions] = useState<SelectorOption[]>([]);
  const [companyControlOptions, setCompanyControlOptions] = useState<SelectorOption[]>([]);
  const [recipientOptions, setRecipientOptions] = useState<SelectorOption[]>([]);
  const [roleOptions, setRoleOptions] = useState<SelectorOption[]>([]);

  const { register, watch, setValue, reset, handleSubmit, formState: { isDirty, errors } } =
    useForm<AttendanceConfigurationFormValues>({
      resolver: zodResolver(attendanceConfigurationSchema),
      mode: 'onChange',
      defaultValues: DEFAULT_VALUES,
    });

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [
        configResponse,
        markingEventsResponse,
        companyControlsResponse,
        recipientsResponse,
        rolesResponse,
      ] = await Promise.all([
        attendanceConfigurationService.getConfiguration(),
        markingEventService.getMarkingEvents(),
        companyControlsService.getCompanyControls(),
        incidentNotificationRecipientsService.getRecipients(),
        roleCatalogService.getRoles(),
      ]);

      if (configResponse.success && configResponse.data) {
        setConfiguration(configResponse.data);
      } else {
        showApiError(configResponse);
      }

      if (markingEventsResponse.success && markingEventsResponse.data) {
        setMarkingEventOptions(
          markingEventsResponse.data.map(item => ({
            value: String(item.id),
            label: item.description,
          })),
        );
      }

      if (companyControlsResponse.success && companyControlsResponse.data) {
        setCompanyControlOptions(
          companyControlsResponse.data.map(item => ({
            value: String(item.id),
            label: item.description,
          })),
        );
      }

      if (recipientsResponse.success && recipientsResponse.data) {
        setRecipientOptions(
          recipientsResponse.data.map(item => ({
            value: String(item.id),
            label: item.description,
          })),
        );
      }

      if (rolesResponse.success && rolesResponse.data) {
        setRoleOptions(
          rolesResponse.data.map(item => ({
            value: String(item.id),
            label: item.description,
          })),
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [showApiError]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Populate form once configuration is loaded
  useEffect(() => {
    if (configuration) {
      reset(configuration);
    }
  }, [configuration, reset]);

  const eventDefinitionMethodOptions = useMemo<SelectorOption[]>(() => [
    { value: 'S', label: translate('configuration.catalogOptions.eventDefinitionMethod.simple') },
    { value: 'H', label: translate('configuration.catalogOptions.eventDefinitionMethod.schedule') },
    { value: 'U', label: translate('configuration.catalogOptions.eventDefinitionMethod.singleEntry') },
  ], [translate]);

  const halfDayAbsenteeismOptions = useMemo<SelectorOption[]>(() => [
    { value: '0', label: translate('configuration.catalogOptions.halfDayAbsenteeism.normalDay') },
    { value: '1', label: translate('configuration.catalogOptions.halfDayAbsenteeism.asVacation') },
    { value: '2', label: translate('configuration.catalogOptions.halfDayAbsenteeism.applies') },
    { value: '3', label: translate('configuration.catalogOptions.halfDayAbsenteeism.byFormula') },
  ], [translate]);

  const automaticModeOptions = useMemo<SelectorOption[]>(() => [
    { value: '', label: translate('configuration.catalogOptions.automaticMode.disabled') },
    { value: 'C', label: translate('configuration.catalogOptions.automaticMode.compensation') },
    { value: 'P', label: translate('configuration.catalogOptions.automaticMode.payment') },
  ], [translate]);

  const handleConfirmReset = useCallback(() => {
    if (configuration) {
      reset(configuration);
    }
  }, [configuration, reset]);

  const onSubmit = useCallback(async (formValues: AttendanceConfigurationFormValues) => {
    setIsSaving(true);
    const input = mapAttendanceConfigurationFormToUpdate(
      formValues,
      configuration?.reviewerFlowProfileId ?? null,
      configuration?.approverFlowProfileId ?? null,
    );
    const response = await attendanceConfigurationService.updateConfiguration(input);
    setIsSaving(false);

    if (!response.success) {
      showError(response.error?.detail ?? translate('common:errors.unexpected'));
      return;
    }

    showSuccess(translate('configuration.actions.saveSuccess'));
    reset(formValues);
    const updatedConfiguration: AttendanceConfiguration = {
      ...(configuration ?? {}),
      ...formValues,
      reviewerFlowProfileId: configuration?.reviewerFlowProfileId ?? null,
      approverFlowProfileId: configuration?.approverFlowProfileId ?? null,
    };
    setConfiguration(updatedConfiguration);
  }, [configuration, reset, showError, showSuccess, translate]);

  const onInvalid = useCallback((fieldErrors: FieldErrors<AttendanceConfigurationFormValues>) => {
    if (!onValidationError) return;
    const errorFields = Object.keys(fieldErrors) as (keyof AttendanceConfigurationFormValues)[];
    const errorTabs = errorFields
      .map(field => FIELD_TAB_MAP[field])
      .filter((tab): tab is TabId => Boolean(tab));
    const firstErrorTab = TAB_ORDER.find(tab => errorTabs.includes(tab));
    if (firstErrorTab) {
      onValidationError(firstErrorTab);
    }
  }, [onValidationError]);

  const handleSave = useCallback(() => {
    void handleSubmit(onSubmit, onInvalid)();
  }, [handleSubmit, onSubmit, onInvalid]);

  const { handleCancelWithWarning: handleCancel, confirmNavigation } = useUnsavedChangesWarning({
    onConfirmLeave: handleConfirmReset,
    isDirty,
  });

  return {
    configuration,
    isLoading,
    isSaving,
    markingEventOptions,
    companyControlOptions,
    recipientOptions,
    roleOptions,
    eventDefinitionMethodOptions,
    halfDayAbsenteeismOptions,
    automaticModeOptions,
    form: { register, watch, setValue, errors },
    isDirty,
    handleSave,
    handleCancel,
    confirmNavigation,
  };
}

export default useAttendanceConfiguration;
